import { Link } from "react-router-dom";
import { Rating } from "@smastrom/react-rating";
import "@smastrom/react-rating/style.css";
import Avatar from "./Avatar";
import FollowButton from "./FollowButton";
import UserBadge from "./UserBadge";
import { Utils } from "../utils";

interface Aggregate {
  avg: number;
  count: number;
}

interface Props {
  seller: any;
  aggregate?: Aggregate | null;
  showViewProfile?: boolean;
}

function AboutSeller({ seller, aggregate, showViewProfile = false }: Props) {
  const hasRating = (aggregate?.count ?? 0) > 0;

  // Same shape as SellerCard's meta strip — sales + speciality on one
  // row, location + distance on another. Keeps the listing card and the
  // profile header in sync so users don't lose information when they
  // click through.
  const sales: number = seller?.salesCount ?? seller?.sold ?? 0;
  const locationText = [seller?.city?.trim(), seller?.state?.trim()]
    .filter(Boolean)
    .join(", ");
  const distanceText = seller?.distance
    ? `${Utils.formatToNumber2(String(seller.distance))} mi`
    : "";
  const locationAndDistance = [locationText, distanceText]
    .filter(Boolean)
    .join(" · ");

  const hasMeta = sales > 0 || seller?.speciality || locationAndDistance;

  return (
    <div className="aboutSellerCard mb-4">
      <div className="aboutSellerCard__top">
        <Avatar
          src={seller?.fileUrl}
          name={seller?.name}
          className="aboutSellerCard__avatar"
        />

        <div className="aboutSellerCard__identity">
          <h2 className="aboutSellerCard__name">{seller?.name}</h2>

          {seller?.badges?.length > 0 && (
            <div className="aboutSellerCard__badges">
              {seller?.badges?.map((b: string) => (
                <UserBadge key={b} badge={b} size="large" />
              ))}
            </div>
          )}

          {hasRating && aggregate && (
            <div className="aboutSellerCard__rating">
              <Rating
                style={{ maxWidth: 90 }}
                value={aggregate.avg}
                readOnly
              />
              <span className="aboutSellerCard__ratingText">
                <strong>{aggregate.avg.toFixed(1)}</strong>
                <span className="aboutSellerCard__ratingSep">·</span>
                {aggregate.count} review
                {aggregate.count === 1 ? "" : "s"}
              </span>
            </div>
          )}

          {hasMeta && (
            <div className="aboutSellerCard__meta">
              {sales > 0 && (
                <span className="aboutSellerCard__pill aboutSellerCard__pill--sales">
                  <i className="bi bi-trophy" aria-hidden="true" />
                  {sales} {sales === 1 ? "sale" : "sales"}
                </span>
              )}
              {seller?.speciality && (
                <span
                  className="aboutSellerCard__pill"
                  title={seller.speciality}
                >
                  <i className="bi bi-palette" aria-hidden="true" />
                  {seller.speciality}
                </span>
              )}
              {locationAndDistance && (
                <span
                  className="aboutSellerCard__pill"
                  title={locationAndDistance}
                >
                  <i className="bi bi-geo-alt" aria-hidden="true" />
                  {locationAndDistance}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="aboutSellerCard__action">
          {showViewProfile && seller?._id && (
            <Link
              to={`/seller/${seller._id}`}
              className="aboutSellerCard__viewProfile"
            >
              View profile
            </Link>
          )}
          <FollowButton sellerId={seller?._id} />
        </div>
      </div>

      {seller?.description && (
        <>
          <div className="aboutSellerCard__divider" />
          <div className="aboutSellerCard__heading">About</div>
          <p className="aboutSellerCard__description">{seller?.description}</p>
        </>
      )}
    </div>
  );
}

export default AboutSeller;
